// CC-53 — /experiments/compare?a=<id>&b=<id>
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/experiments";

function BriefMeta({ b, side }) {
  if (!b) return null;
  return (
    <Card data-testid={`brief-meta-${side}`}>
      <CardHeader>
        <CardTitle className="text-base">
          Brief {side.toUpperCase()}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div><span className="text-muted-foreground">ID:</span> <code className="text-xs">{b.id}</code></div>
        <div><span className="text-muted-foreground">Date:</span> {b.date}</div>
        <div>
          <span className="text-muted-foreground">Status:</span>&nbsp;
          <Badge variant={b.status === "experimental" ? "secondary" : "outline"}>
            {b.status}
          </Badge>
        </div>
        <div>
          <span className="text-muted-foreground">Label:</span>&nbsp;
          <code className="text-xs">{b.experiment_label || "—"}</code>
        </div>
        <div><span className="text-muted-foreground">Articles:</span> {b.total_articles}</div>
      </CardContent>
    </Card>
  );
}

function ArticleList({ title, items, color, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader>
        <CardTitle className="text-base">
          {title} <span className="text-muted-foreground">({items.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 && (
          <div className="text-sm text-muted-foreground">None</div>
        )}
        {items.map((a, i) => (
          <div key={i} className="text-sm border-l-4 pl-3 py-1" style={{ borderColor: color }}>
            <div className="font-medium truncate">{a.title}</div>
            <div className="text-xs text-muted-foreground flex gap-2">
              <span>{a.segment}</span>
              {a.url && (
                <a className="underline truncate" href={a.url} target="_blank" rel="noreferrer">
                  {a.url}
                </a>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function ExperimentsCompare() {
  const [params] = useSearchParams();
  const a = params.get("a");
  const b = params.get("b");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/compare?a=${a}&b=${b}`, { credentials: "include" });
        if (!r.ok) throw new Error(await r.text());
        setData(await r.json());
      } catch (e) {
        toast.error("Compare failed", { description: String(e) });
      } finally { setLoading(false); }
    })();
  }, [a, b]);

  const promote = async (id, side) => {
    setPromoting(side);
    try {
      const r = await fetch(`${API}/promote/${id}`, {
        method: "POST", credentials: "include",
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success(`Brief ${side.toUpperCase()} promoted to generated`);
    } catch (e) {
      toast.error("Promote failed", { description: String(e) });
    } finally { setPromoting(""); }
  };

  if (loading) return <div className="p-6">Loading comparison…</div>;
  if (!data)   return <div className="p-6">No comparison data.</div>;

  return (
    <div className="p-6 space-y-5" data-testid="experiments-compare-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Compare Briefs</h1>
        <Link to="/experiments"><Button variant="outline">← Back</Button></Link>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <BriefMeta b={data.brief_a} side="a" />
          <div className="mt-2">
            {data.brief_a?.status === "experimental" && (
              <Button
                size="sm"
                disabled={promoting !== ""}
                onClick={() => promote(data.brief_a.id, "a")}
                data-testid="promote-a-btn"
              >
                {promoting === "a" ? "Promoting…" : "Promote A to Production"}
              </Button>
            )}
          </div>
        </div>
        <div>
          <BriefMeta b={data.brief_b} side="b" />
          <div className="mt-2">
            {data.brief_b?.status === "experimental" && (
              <Button
                size="sm"
                disabled={promoting !== ""}
                onClick={() => promote(data.brief_b.id, "b")}
                data-testid="promote-b-btn"
              >
                {promoting === "b" ? "Promoting…" : "Promote B to Production"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card data-testid="overlap-card">
        <CardHeader>
          <CardTitle className="text-base">Overlap</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-6">
          <div>
            <div className="text-4xl font-bold">{data.overlap_pct}%</div>
            <div className="text-xs text-muted-foreground">URL overlap (canonical)</div>
          </div>
          <div className="text-sm space-y-1">
            <div>Common: <b>{data.common_count}</b></div>
            <div>Unique to A: <b>{data.unique_a_count}</b></div>
            <div>Unique to B: <b>{data.unique_b_count}</b></div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <ArticleList title="Unique to A"  items={data.unique_to_a} color="#22c55e" testid="unique-a-list" />
        <ArticleList title="Unique to B"  items={data.unique_to_b} color="#3b82f6" testid="unique-b-list" />
      </div>

      <ArticleList title="Common articles" items={data.common_articles} color="#71717a" testid="common-list" />

      <Card data-testid="coverage-card">
        <CardHeader>
          <CardTitle className="text-base">Coverage per segment</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-2 text-left">Segment</th>
                <th className="p-2 text-right">A</th>
                <th className="p-2 text-right">B</th>
                <th className="p-2 text-right">Common</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.coverage_per_segment || {}).map(([seg, v]) => (
                <tr key={seg} className="border-t">
                  <td className="p-2">{seg}</td>
                  <td className="p-2 text-right">{v.a_count}</td>
                  <td className="p-2 text-right">{v.b_count}</td>
                  <td className="p-2 text-right">{v.common}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card data-testid="tier-card">
        <CardHeader>
          <CardTitle className="text-base">Tier distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-2 text-left">Side</th>
                <th className="p-2 text-right">Tier 1</th>
                <th className="p-2 text-right">Tier 2</th>
                <th className="p-2 text-right">Tier 3</th>
                <th className="p-2 text-right">None</th>
              </tr>
            </thead>
            <tbody>
              {["a", "b"].map((s) => (
                <tr key={s} className="border-t">
                  <td className="p-2 uppercase">{s}</td>
                  <td className="p-2 text-right">{data.tier_distribution?.[s]?.tier_1 ?? 0}</td>
                  <td className="p-2 text-right">{data.tier_distribution?.[s]?.tier_2 ?? 0}</td>
                  <td className="p-2 text-right">{data.tier_distribution?.[s]?.tier_3 ?? 0}</td>
                  <td className="p-2 text-right">{data.tier_distribution?.[s]?.none ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
